import { useEffect, useState, useCallback } from "react";
import { Loader2, Check, Plus, RotateCcw, Activity, Trash2, Ticket, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Navigate } from "react-router-dom";
import Footer from "@/components/Footer";
import MobileBottomNav from "@/components/MobileBottomNav";

export default function AdminSubscriptionsPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletedGroups, setDeletedGroups] = useState(null);
  const [deletedLoading, setDeletedLoading] = useState(true);
  const [restoringId, setRestoringId] = useState("");
  const [deletedUsers, setDeletedUsers] = useState(null);
  const [restoringUserId, setRestoringUserId] = useState("");
  const [paypalHealth, setPaypalHealth] = useState(null);
  const [paypalChecking, setPaypalChecking] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/subscriptions");
      setData(r.data);
    } catch (_e) {
      void _e;
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeleted = useCallback(async () => {
    setDeletedLoading(true);
    try {
      const r = await api.get("/admin/deleted-groups");
      setDeletedGroups(r.data.deleted_groups || []);
    } catch (_e) {
      void _e;
      setDeletedGroups([]);
    } finally {
      setDeletedLoading(false);
    }
  }, []);

  const loadDeletedUsers = useCallback(async () => {
    try {
      const r = await api.get("/admin/deleted-users");
      setDeletedUsers(r.data.deleted_users || []);
    } catch (_e) {
      void _e;
      setDeletedUsers([]);
    }
  }, []);

  useEffect(() => { load(); loadDeleted(); loadDeletedUsers(); }, [load, loadDeleted, loadDeletedUsers]);

  if (user && user.role !== "admin") return <Navigate to="/couples" replace />;

  const confirm = async (id) => {
    try {
      await api.post("/admin/subscriptions/confirm", { intent_id: id });
      toast.success("Confirmed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const extend = async (userId, plan) => {
    try {
      await api.post(`/admin/subscriptions/${userId}/extend`, { plan });
      toast.success(`Extended (${plan})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const r = await api.post(`/admin/users/${u.user_id}/reset-password`);
      const tmp = r.data.temporary_password;
      window.prompt(`Temporary password for ${u.email} — COPY IT NOW:`, tmp);
      toast.success("Password reset. Share securely with user.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const restoreGroup = async (snap) => {
    const msg = t("admin.restore_confirm").replace("{name}", snap.group_name || "(unnamed)");
    if (!window.confirm(msg)) return;
    setRestoringId(snap.id);
    try {
      await api.post(`/admin/deleted-groups/${snap.id}/restore`);
      toast.success(t("admin.restore_success"));
      await loadDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setRestoringId("");
    }
  };

  const runPaypalHealth = async () => {
    setPaypalChecking(true);
    setPaypalHealth(null);
    try {
      const r = await api.get("/admin/paypal/health");
      setPaypalHealth(r.data);
      const ok = r.data?.oauth?.ok && r.data?.create_order?.ok;
      if (ok) toast.success("PayPal OK — credentials work and order can be created.");
      else toast.error("PayPal check FAILED — see details below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    } finally {
      setPaypalChecking(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeletingUser(true);
    try {
      const r = await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(t("admin.delete_user_success").replace("{email}", r.data?.email || deleteTarget.email));
      setDeleteTarget(null);
      await load();
      await loadDeletedUsers();
      await loadDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingUser(false);
    }
  };

  const restoreUser = async (snap) => {
    const msg = t("admin.restore_user_confirm").replace("{email}", snap.email || "(unknown)");
    if (!window.confirm(msg)) return;
    setRestoringUserId(snap.id);
    try {
      const r = await api.post(`/admin/deleted-users/${snap.id}/restore`);
      toast.success(t("admin.restore_user_success").replace("{email}", r.data?.email || snap.email));
      await loadDeletedUsers();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringUserId("");
    }
  };

  return (
    <div className="min-h-screen bg-cream/30 px-4 sm:px-8 py-8 pb-24 md:pb-8">
      <MobileBottomNav />
      <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-subs-page">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
          {t("sub.admin_panel")}
        </h1>

        {/* Pending */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-2">
            {t("sub.admin_pending")} ({data?.pending?.length || 0})
          </h2>
          <div className="bg-white rounded-xl border border-app-border overflow-hidden">
            {loading ? (
              <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline-block text-espresso/60" /></div>
            ) : data?.pending?.length ? (
              <table className="w-full text-sm">
                <thead className="bg-cream-2 text-xs uppercase tracking-wider text-olive">
                  <tr>
                    <th className="px-3 py-2 text-left">User</th>
                    <th className="px-3 py-2 text-left">Plan</th>
                    <th className="px-3 py-2 text-left">Amount</th>
                    <th className="px-3 py-2 text-left">{t("sub.method_col")}</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Submitted</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending.map((p) => (
                    <tr key={p.id} className="border-t border-app-border" data-testid={`pending-${p.id}`}>
                      <td className="px-3 py-2">{p.user_email}</td>
                      <td className="px-3 py-2 font-bold">{p.plan}</td>
                      <td className="px-3 py-2">{p.amount} {p.currency}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-bold ${p.method === "paypal_link" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>
                          {p.method === "paypal_link" ? "PayPal link" : "BenefitPay"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.reference}</td>
                      <td className="px-3 py-2 text-xs text-espresso/60">
                        {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          onClick={() => confirm(p.id)}
                          size="sm"
                          className="bg-olive hover:bg-olive/90"
                          data-testid={`confirm-${p.id}`}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          {t("sub.admin_confirm")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-espresso/50">No pending payments.</div>
            )}
          </div>
        </div>

        <CodesSection />

        {/* Users */}
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h2 className="text-xs font-bold uppercase tracking-widest text-olive mr-2">
              {t("sub.admin_users")} ({data?.users?.length || 0})
            </h2>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email or name…"
              className="flex-1 min-w-[180px] max-w-[300px] bg-white border border-app-border rounded px-2 py-1.5 text-sm outline-none focus:border-olive"
              data-testid="user-search"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-app-border rounded px-2 py-1.5 text-sm outline-none focus:border-olive"
              data-testid="status-filter"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="expired">Expired</option>
              <option value="none">No subscription yet</option>
            </select>
          </div>
          <div className="bg-white rounded-xl border border-app-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream-2 text-xs uppercase tracking-wider text-olive">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">Expires</th>
                  <th className="px-3 py-2 text-left">Days left</th>
                  <th className="px-3 py-2">{t("sub.admin_extend")}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data?.users
                  ?.filter((u) => {
                    if (statusFilter !== "all" && u.status !== statusFilter) return false;
                    if (!query.trim()) return true;
                    const q = query.toLowerCase();
                    return (
                      (u.email || "").toLowerCase().includes(q) ||
                      (u.name || "").toLowerCase().includes(q)
                    );
                  })
                  .map((u) => (
                  <tr key={u.user_id} className="border-t border-app-border" data-testid={`user-${u.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{u.name || u.email.split("@")[0]}</div>
                      <div className="text-xs text-espresso/60">{u.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        u.status === "active" ? "bg-olive/15 text-olive" :
                        u.status === "trial" ? "bg-amber-100 text-amber-800" :
                        "bg-danger/15 text-danger"
                      }`}>{u.status}</span>
                    </td>
                    <td className="px-3 py-2">{u.plan}</td>
                    <td className="px-3 py-2 text-xs">
                      {u.end_date ? new Date(u.end_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">{u.days_left}</td>
                    <td className="px-3 py-2">
                      <Select onValueChange={(plan) => extend(u.user_id, plan)}>
                        <SelectTrigger className="h-8 w-28">
                          <SelectValue placeholder="+plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1m">+1 month</SelectItem>
                          <SelectItem value="3m">+3 months</SelectItem>
                          <SelectItem value="6m">+6 months</SelectItem>
                          <SelectItem value="1y">+1 year</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Button
                          onClick={() => resetPassword(u)}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          data-testid={`reset-pwd-${u.user_id}`}
                        >
                          {t("auth.admin_reset_btn")}
                        </Button>
                        <Button
                          onClick={() => setDeleteTarget(u)}
                          size="sm"
                          variant="outline"
                          className="text-xs border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                          data-testid={`delete-user-${u.user_id}`}
                          title={t("admin.delete_user_btn")}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* PayPal health check */}
        <div data-testid="paypal-health-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-bold uppercase tracking-widest text-olive">
              PayPal LIVE diagnostics
            </h2>
            <Button
              onClick={runPaypalHealth}
              disabled={paypalChecking}
              size="sm"
              variant="outline"
              className="border-olive text-olive hover:bg-olive/10"
              data-testid="paypal-health-btn"
            >
              {paypalChecking ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <Activity className="w-3.5 h-3.5 mr-1" />
                  Run health check
                </>
              )}
            </Button>
          </div>
          {paypalHealth && (
            <div
              className="bg-white rounded-xl border border-app-border p-3 text-xs space-y-1.5 font-mono"
              data-testid="paypal-health-result"
            >
              {paypalHealth.mode !== "live" && (
                <div
                  className="bg-red-100 border border-red-300 text-red-900 rounded p-2 font-sans font-semibold"
                  data-testid="paypal-mode-warning"
                >
                  ⚠️ PAYPAL_MODE is <b>{paypalHealth.mode}</b>, not <b>live</b>. Real payments will not work in this environment. Update the PAYPAL_MODE and credentials in your production environment config, then redeploy.
                </div>
              )}
              <div>Mode: <b>{paypalHealth.mode}</b> · Base: {paypalHealth.base}</div>
              <div>Client: {paypalHealth.client_id_prefix} · Secret present: {String(paypalHealth.has_secret)}</div>
              <div>OAuth: {paypalHealth.oauth?.ok ? "✅" : "❌"} (HTTP {paypalHealth.oauth?.status}){!paypalHealth.oauth?.ok && paypalHealth.oauth?.debug_id ? ` · debug_id=${paypalHealth.oauth.debug_id}` : ""}</div>
              {paypalHealth.oauth?.body_snippet && <div className="text-danger break-all">{paypalHealth.oauth.body_snippet}</div>}
              <div>Create order: {paypalHealth.create_order?.ok ? "✅" : "❌"} (HTTP {paypalHealth.create_order?.status})</div>
              {paypalHealth.create_order?.order_id && <div>Order ID: <span className="text-olive">{paypalHealth.create_order.order_id}</span> (NOT captured, no money moved)</div>}
              {!paypalHealth.create_order?.ok && paypalHealth.create_order?.debug_id && <div>debug_id: <span className="text-danger">{paypalHealth.create_order.debug_id}</span></div>}
              {paypalHealth.create_order?.name && <div>Error name: <span className="text-danger">{paypalHealth.create_order.name}</span></div>}
              {paypalHealth.create_order?.message && <div>Message: {paypalHealth.create_order.message}</div>}
              {Array.isArray(paypalHealth.create_order?.details) && paypalHealth.create_order.details.map((d, i) => (
                <div key={i} className="text-danger">→ {d.issue || d.field}: {d.description}</div>
              ))}
            </div>
          )}
        </div>

        {/* Deleted users recovery */}
        <div data-testid="deleted-users-section">
          <h2 className="text-xs font-bold uppercase tracking-widest text-red-700 mb-2">
            {t("admin.deleted_users")} ({deletedUsers?.length || 0})
          </h2>
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            {deletedUsers === null ? (
              <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline-block text-espresso/60" /></div>
            ) : deletedUsers.length ? (
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-xs uppercase tracking-wider text-red-800">
                  <tr>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_user_email_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_user_role_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_user_pwd_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_when_col")}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers.map((s) => (
                    <tr key={s.id} className="border-t border-app-border" data-testid={`deleted-user-${s.id}`}>
                      <td className="px-3 py-2 font-semibold">{s.email || "(unknown)"}</td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider">{s.role}</td>
                      <td className="px-3 py-2 text-xs">
                        {s.has_password ? "✅ yes" : (s.auth_provider === "google" ? "🔵 Google" : "—")}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {s.deleted_at ? new Date(s.deleted_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          onClick={() => restoreUser(s)}
                          disabled={restoringUserId === s.id}
                          size="sm"
                          variant="outline"
                          className="text-xs border-olive text-olive hover:bg-olive/10"
                          data-testid={`restore-user-${s.id}`}
                        >
                          {restoringUserId === s.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              {t("admin.restore_user_btn")}
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-espresso/60">{t("admin.deleted_users_empty")}</div>
            )}
          </div>
        </div>

        {/* Deleted groups recovery */}
        <div data-testid="deleted-groups-section">
          <h2 className="text-xs font-bold uppercase tracking-widest text-red-700 mb-2">
            {t("admin.deleted_groups")} ({deletedGroups?.length || 0})
          </h2>
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            {deletedLoading ? (
              <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline-block text-espresso/60" /></div>
            ) : deletedGroups?.length ? (
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-xs uppercase tracking-wider text-red-800">
                  <tr>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_group_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_invite_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_data_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_by_col")}</th>
                    <th className="px-3 py-2 text-left">{t("admin.deleted_when_col")}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {deletedGroups.map((s) => (
                    <tr key={s.id} className="border-t border-app-border" data-testid={`deleted-group-${s.id}`}>
                      <td className="px-3 py-2 font-semibold">{s.group_name || "(unnamed)"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{s.invite_code || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <div>{t("admin.deleted_couples").replace("{n}", s.couples_with_data)}</div>
                        <div>{t("admin.deleted_birds").replace("{n}", s.birds_with_data)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{s.deleted_by_email || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {s.deleted_at ? new Date(s.deleted_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          onClick={() => restoreGroup(s)}
                          disabled={restoringId === s.id}
                          size="sm"
                          variant="outline"
                          className="text-xs border-olive text-olive hover:bg-olive/10"
                          data-testid={`restore-group-${s.id}`}
                        >
                          {restoringId === s.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              {t("admin.deleted_restore_btn")}
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-espresso/60">{t("admin.deleted_empty")}</div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !deletingUser && !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {t("admin.delete_user_title").replace("{email}", deleteTarget?.email || "")}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("admin.delete_user_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser} data-testid="admin-delete-user-no">
              {t("admin.delete_user_no")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-delete-user-yes"
            >
              {deletingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.delete_user_yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function CodesSection() {
  const [codes, setCodes] = useState(null);
  const [count, setCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [showUsed, setShowUsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/codes");
      setCodes(r.data.codes || []);
    } catch (_e) {
      void _e;
      setCodes([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/admin/codes/generate", { count: Number(count) || 1 });
      toast.success(`Generated ${r.data.codes.length} code(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = async (c) => {
    try {
      await navigator.clipboard.writeText(c);
      toast.success(`Copied ${c}`);
    } catch (_e) {
      void _e;
    }
  };

  const removeCode = async (c) => {
    if (!window.confirm(`Delete unused code ${c}?`)) return;
    try {
      await api.delete(`/admin/codes/${c}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const list = (codes || []).filter((c) => (showUsed ? true : c.active));
  const usedCount = (codes || []).filter((c) => !c.active).length;

  return (
    <div data-testid="admin-codes-section">
      <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-2 flex items-center gap-1.5">
        <Ticket className="w-3.5 h-3.5" />
        Extension codes (+10 days · single-use · 1 per user / 10 days · each code valid 9 days)
      </h2>
      <div className="bg-white rounded-xl border border-app-border p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-20 bg-white border border-app-border rounded px-2 py-1.5 text-sm outline-none focus:border-olive"
            data-testid="codes-count-input"
          />
          <Button
            onClick={generate}
            disabled={generating}
            size="sm"
            className="bg-olive hover:bg-olive/90"
            data-testid="codes-generate-button"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-3.5 h-3.5 mr-1" />Generate</>)}
          </Button>
          <label className="ml-auto flex items-center gap-1.5 text-xs text-espresso/70 cursor-pointer">
            <input
              type="checkbox"
              checked={showUsed}
              onChange={(e) => setShowUsed(e.target.checked)}
              className="accent-[#6B8E4F]"
              data-testid="codes-show-used"
            />
            Show used ({usedCount})
          </label>
        </div>
        <div className="border border-app-border rounded-lg max-h-56 overflow-y-auto divide-y divide-app-border/60" data-testid="codes-list">
          {codes === null ? (
            <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline-block text-espresso/50" /></div>
          ) : list.length === 0 ? (
            <div className="px-3 py-3 text-xs text-espresso/50">No codes yet — generate some above.</div>
          ) : (
            list.map((c) => (
              <div key={c.code} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className="font-mono font-semibold">{c.code}</span>
                {!c.active ? (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-espresso/10 text-espresso/60">
                    used · {c.used_by_email}
                  </span>
                ) : c.expired ? (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700">expired</span>
                ) : (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-olive/10 text-olive">unused</span>
                )}
                {c.active && !c.expired && c.expires_at && (
                  <span className="text-[10px] text-espresso/40">
                    expires {new Date(c.expires_at).toLocaleDateString()}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => copyCode(c.code)}
                    className="p-1 text-espresso/40 hover:text-espresso"
                    title="Copy"
                    data-testid={`code-copy-${c.code}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  {c.active && (
                    <button
                      type="button"
                      onClick={() => removeCode(c.code)}
                      className="p-1 text-espresso/40 hover:text-red-600"
                      title="Delete"
                      data-testid={`code-delete-${c.code}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
